import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ProfileFormData {
  fullname: string;
  email: string;
  phone: string;
  gender: string | number | null;
  birthday: string;
  job_title: string;
  avatar?: string;
  avatar_url?: string;
}

interface ProfileFormProps {
  initialData?: Partial<ProfileFormData>;
  onSubmit: (data: ProfileFormData) => void;
  onCancel?: () => void;
  loading?: boolean;
}

const ProfileForm: React.FC<ProfileFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ProfileFormData>({
    fullname: initialData.fullname || '',
    email: initialData.email || '',
    phone: initialData.phone || '',
    gender: initialData.gender || null,
    birthday: initialData.birthday || '',
    job_title: initialData.job_title || '',
    avatar: initialData.avatar || '',
  });
  const [avatarPreview, setAvatarPreview] = useState<string>(initialData.avatar_url || '');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'gender') {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    }
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setAvatarPreview(base64);
        setFormData(prev => ({ ...prev, avatar: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('profile.profileImage', 'Ảnh đại diện')}
        </label>
        <div className="relative inline-block">
          <div className="w-28 h-28 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Avatar preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <svg
                  className="w-16 h-16 text-gray-300 dark:text-gray-500"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
            )}
          </div>
          <label
            htmlFor="avatar-upload"
            className="absolute bottom-0 right-0 w-8 h-8 bg-blue-500 hover:bg-blue-600 rounded-full flex items-center justify-center cursor-pointer shadow-lg transition-colors"
          >
            <svg
              className="w-4 h-4 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </label>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Full Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('full_name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="fullname"
            //disabled
            value={formData.fullname}
            onChange={handleChange}
            placeholder={t('profile.enterFullName', 'Nhập họ và tên')}
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-md !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-white !placeholder-gray-400 focus:ring-2 focus:!ring-blue-500 focus:border-transparent transition-all"
            required
          />
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('email')} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            disabled
            value={formData.email}
            onChange={handleChange}
            placeholder={t('profile.enterEmail', 'Nhập địa chỉ email')}
            className="w-full px-4 py-2.5 border cursor-not-allowed border-gray-200 dark:border-gray-600 rounded-md !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-white !placeholder-gray-400 focus:ring-2 focus:!ring-blue-500 focus:border-transparent transition-all"
            required
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('phone')}
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={(e) => {
              const regex = /^[0-9\b]+$/;
              if (e.target.value === '' || regex.test(e.target.value)) {
                handleChange(e);
              }
            }}
            placeholder={t('profile.enterPhone', 'Nhập số điện thoại')}
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-md !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-white !placeholder-gray-400 focus:ring-2 focus:!ring-blue-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Gender */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('gender')}
          </label>
          <select
            name="gender"
            value={formData.gender || ''}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-md !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-white focus:ring-2 focus:!ring-blue-500 focus:border-transparent transition-all"
          >
            <option disabled value="">{t('profile.selectGender', 'Chọn giới tính')}</option>
            <option value="1">{t('profile.male', 'Nam')}</option>
            <option value="2">{t('profile.female', 'Nữ')}</option>
          </select>
        </div>

        {/* Birthday */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('date_of_birth', 'Sinh nhật')}
          </label>
          <input
            type="date"
            name="birthday"
            value={formData.birthday.split(' ')[0]}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-md !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-white focus:ring-2 focus:!ring-blue-500 focus:border-transparent transition-all"
          />
        </div>

        {/* Occupation */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {t('career')}
          </label>
          <input
            type="text"
            name="job_title"
            value={formData.job_title}
            onChange={handleChange}
            placeholder={t('profile.enterOccupation', 'Nhập nghề nghiệp')}
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-md !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-white !placeholder-gray-400 focus:ring-2 focus:!ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2.5 border !border-red-500 !text-red-500 rounded-md hover:!bg-red-50 dark:hover:!bg-red-900/20 font-medium transition-colors"
          >
            {t('cancel')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading}
          className="px-8 py-2.5 !bg-blue-500 hover:!bg-blue-600 !text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? t('saving') : t('save')}
        </button>
      </div>
    </form>
  );
};

export default ProfileForm;
